/**
 * Matters API Service
 * TanStack Query hooks for matter management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { MatterStage } from '../context/LawityContext';

// Query Keys
export const matterKeys = {
  all: ['matters'] as const,
  lists: () => [...matterKeys.all, 'list'] as const,
  list: (filters?: MatterFilters) => [...matterKeys.lists(), filters] as const,
  detail: (id: string) => [...matterKeys.all, 'detail', id] as const,
  timeline: (id: string) => [...matterKeys.all, 'timeline', id] as const,
  documents: (id: string) => [...matterKeys.all, 'documents', id] as const,
};

// Types
export interface Matter {
  id: string;
  number: string;
  name: string;
  clientId: string;
  clientName: string;
  stage: MatterStage;
  status: 'active' | 'pending' | 'closed' | 'archived';
  type: string;
  description?: string;
  openDate: Date;
  closeDate?: Date;
  responsibleAttorney: string;
  billedAmount: number;
  outstandingBalance: number;
  documentCount: number;
  taskCount: number;
  urgentTaskCount: number;
  nextDeadline?: Date;
  lastActivity: Date;
}

export interface MatterFilters {
  status?: string;
  stage?: MatterStage;
  clientId?: string;
  responsibleAttorney?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface MatterTimelineEvent {
  id: string;
  matterId: string;
  type: 'deadline' | 'hearing' | 'filing' | 'meeting' | 'note' | 'document' | 'billing';
  title: string;
  description?: string;
  date: Date;
  createdBy: string;
  createdAt: Date;
  isComplete?: boolean;
}

export interface CreateMatterInput {
  name: string;
  clientId: string;
  type: string;
  description?: string;
  responsibleAttorney: string;
}

// API Functions
async function fetchMatters(filters?: MatterFilters): Promise<Matter[]> {
  // return api.get<Matter[]>('/matters', { params: filters });

  await new Promise((resolve) => setTimeout(resolve, 300));

  const mockMatters: Matter[] = [
    {
      id: '1',
      number: '2024-0089-REP',
      name: 'Thompson Estate - Probate',
      clientId: 'client-1',
      clientName: 'Sarah Thompson',
      stage: 'closing',
      status: 'active',
      type: 'Estate/Probate',
      openDate: new Date(Date.now() - 86400000 * 90),
      responsibleAttorney: 'Sarah Johnson',
      billedAmount: 45000,
      outstandingBalance: 2500,
      documentCount: 34,
      taskCount: 12,
      urgentTaskCount: 2,
      nextDeadline: new Date(Date.now() + 86400000 * 5),
      lastActivity: new Date(Date.now() - 86400000),
    },
    {
      id: '2',
      number: '2024-0092-LIT',
      name: 'Martinez v. Acme Corp',
      clientId: 'client-2',
      clientName: 'Carlos Martinez',
      stage: 'litigation',
      status: 'active',
      type: 'Personal Injury',
      openDate: new Date(Date.now() - 86400000 * 180),
      responsibleAttorney: 'Sarah Johnson',
      billedAmount: 18500,
      outstandingBalance: 3200,
      documentCount: 156,
      taskCount: 28,
      urgentTaskCount: 5,
      nextDeadline: new Date(Date.now() + 86400000 * 14),
      lastActivity: new Date(Date.now() - 3600000 * 2),
    },
    {
      id: '3',
      number: '2024-0095-BUF',
      name: 'Riverside Holdings - Acquisition',
      clientId: 'client-3',
      clientName: 'Riverside Holdings LLC',
      stage: 'closing',
      status: 'active',
      type: 'M&A',
      openDate: new Date(Date.now() - 86400000 * 45),
      responsibleAttorney: 'Sarah Johnson',
      billedAmount: 125000,
      outstandingBalance: 0,
      documentCount: 89,
      taskCount: 18,
      urgentTaskCount: 3,
      nextDeadline: new Date(Date.now() + 86400000 * 2),
      lastActivity: new Date(Date.now() - 3600000),
    },
    {
      id: '4',
      number: '2024-0098-DIS',
      name: 'Johnson Estate Planning',
      clientId: 'client-4',
      clientName: 'Robert Johnson',
      stage: 'intake',
      status: 'pending',
      type: 'Estate Planning',
      openDate: new Date(Date.now() - 86400000 * 7),
      responsibleAttorney: 'Sarah Johnson',
      billedAmount: 0,
      outstandingBalance: 0,
      documentCount: 4,
      taskCount: 8,
      urgentTaskCount: 0,
      lastActivity: new Date(Date.now() - 86400000 * 2),
    },
    {
      id: '5',
      number: '2023-0156-EST',
      name: 'Williams Trust Administration',
      clientId: 'client-5',
      clientName: 'Williams Family Trust',
      stage: 'archived',
      status: 'closed',
      type: 'Trust Administration',
      openDate: new Date(Date.now() - 86400000 * 365),
      closeDate: new Date(Date.now() - 86400000 * 30),
      responsibleAttorney: 'Sarah Johnson',
      billedAmount: 67000,
      outstandingBalance: 0,
      documentCount: 78,
      taskCount: 0,
      urgentTaskCount: 0,
      lastActivity: new Date(Date.now() - 86400000 * 30),
    },
  ];

  // Apply filters
  let filtered = mockMatters;

  if (filters?.status) {
    filtered = filtered.filter((m) => m.status === filters.status);
  }
  if (filters?.stage) {
    filtered = filtered.filter((m) => m.stage === filters.stage);
  }
  if (filters?.clientId) {
    filtered = filtered.filter((m) => m.clientId === filters.clientId);
  }
  if (filters?.search) {
    const search = filters.search.toLowerCase();
    filtered = filtered.filter(
      (m) =>
        m.name.toLowerCase().includes(search) ||
        m.number.toLowerCase().includes(search) ||
        m.clientName.toLowerCase().includes(search)
    );
  }
  if (filters?.limit) {
    filtered = filtered.slice(filters.offset || 0, (filters.offset || 0) + filters.limit);
  }

  return filtered;
}

async function fetchMatter(id: string): Promise<Matter> {
  // return api.get<Matter>(`/matters/${id}`);

  const matters = await fetchMatters();
  const matter = matters.find((m) => m.id === id);

  if (!matter) {
    throw new Error('Matter not found');
  }

  return matter;
}

async function fetchMatterTimeline(matterId: string): Promise<MatterTimelineEvent[]> {
  // return api.get<MatterTimelineEvent[]>(`/matters/${matterId}/timeline`);

  await new Promise((resolve) => setTimeout(resolve, 250));

  const today = new Date();

  return [
    {
      id: 't1',
      matterId,
      type: 'deadline',
      title: 'Discovery Deadline',
      description: 'Final day to submit discovery responses',
      date: new Date(today.getTime() + 86400000 * 5),
      createdBy: 'System',
      createdAt: new Date(today.getTime() - 86400000 * 30),
    },
    {
      id: 't2',
      matterId,
      type: 'hearing',
      title: 'Motion Hearing',
      description: 'Hearing on Motion to Compel',
      date: new Date(today.getTime() + 86400000 * 14),
      createdBy: 'Court',
      createdAt: new Date(today.getTime() - 86400000 * 7),
    },
    {
      id: 't3',
      matterId,
      type: 'document',
      title: 'Draft Agreement Uploaded',
      date: new Date(today.getTime() - 86400000),
      createdBy: 'Maria Garcia',
      createdAt: new Date(today.getTime() - 86400000),
      isComplete: true,
    },
    {
      id: 't4',
      matterId,
      type: 'meeting',
      title: 'Client Meeting',
      description: 'Strategy session for upcoming deposition',
      date: today,
      createdBy: 'Sarah Johnson',
      createdAt: new Date(today.getTime() - 86400000 * 3),
    },
    {
      id: 't5',
      matterId,
      type: 'filing',
      title: 'Complaint Filed',
      date: new Date(today.getTime() - 86400000 * 60),
      createdBy: 'Sarah Johnson',
      createdAt: new Date(today.getTime() - 86400000 * 60),
      isComplete: true,
    },
  ];
}

async function createMatter(input: CreateMatterInput): Promise<Matter> {
  // return api.post<Matter>('/matters', input);

  await new Promise((resolve) => setTimeout(resolve, 500));

  return {
    id: `matter-${Date.now()}`,
    number: `2024-${Math.floor(Math.random() * 1000).toString().padStart(4, '0')}-NEW`,
    name: input.name,
    clientId: input.clientId,
    clientName: 'New Client',
    stage: 'intake',
    status: 'pending',
    type: input.type,
    description: input.description,
    openDate: new Date(),
    responsibleAttorney: input.responsibleAttorney,
    billedAmount: 0,
    outstandingBalance: 0,
    documentCount: 0,
    taskCount: 0,
    urgentTaskCount: 0,
    lastActivity: new Date(),
  };
}

async function updateMatterStage(matterId: string, stage: MatterStage): Promise<Matter> {
  // return api.patch<Matter>(`/matters/${matterId}`, { stage });

  await new Promise((resolve) => setTimeout(resolve, 200));

  const matter = await fetchMatter(matterId);
  return { ...matter, stage };
}

// Hooks

/**
 * Fetch list of matters
 */
export function useMatters(filters?: MatterFilters) {
  return useQuery({
    queryKey: matterKeys.list(filters),
    queryFn: () => fetchMatters(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch single matter
 */
export function useMatter(id: string) {
  return useQuery({
    queryKey: matterKeys.detail(id),
    queryFn: () => fetchMatter(id),
    enabled: !!id,
  });
}

/**
 * Fetch matter timeline
 */
export function useMatterTimeline(matterId: string) {
  return useQuery({
    queryKey: matterKeys.timeline(matterId),
    queryFn: () => fetchMatterTimeline(matterId),
    enabled: !!matterId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Create a new matter
 */
export function useCreateMatter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMatter,

    onSuccess: (newMatter) => {
      // Add to list cache
      queryClient.setQueryData<Matter[]>(matterKeys.list(), (old) =>
        old ? [newMatter, ...old] : [newMatter]
      );
    },
  });
}

/**
 * Update matter stage
 */
export function useUpdateMatterStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ matterId, stage }: { matterId: string; stage: MatterStage }) =>
      updateMatterStage(matterId, stage),

    onMutate: async ({ matterId, stage }) => {
      await queryClient.cancelQueries({ queryKey: matterKeys.detail(matterId) });

      const previousMatter = queryClient.getQueryData<Matter>(matterKeys.detail(matterId));

      if (previousMatter) {
        queryClient.setQueryData<Matter>(matterKeys.detail(matterId), {
          ...previousMatter,
          stage,
        });
      }

      return { previousMatter };
    },

    onError: (_err, { matterId }, context) => {
      if (context?.previousMatter) {
        queryClient.setQueryData(matterKeys.detail(matterId), context.previousMatter);
      }
    },

    onSettled: (_, __, { matterId }) => {
      queryClient.invalidateQueries({ queryKey: matterKeys.detail(matterId) });
      queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
    },
  });
}

/**
 * Prefetch matter for faster navigation
 */
export function usePrefetchMatter() {
  const queryClient = useQueryClient();

  return (id: string) => {
    queryClient.prefetchQuery({
      queryKey: matterKeys.detail(id),
      queryFn: () => fetchMatter(id),
    });
  };
}

export default {
  useMatters,
  useMatter,
  useMatterTimeline,
  useCreateMatter,
  useUpdateMatterStage,
  usePrefetchMatter,
};
